import axios from 'axios';
import { getApiBaseUrl } from '../config/apiBase';

axios.defaults.withCredentials = true;

const TABLE_TIMEOUT_MS = 120000;
const REPORT_TIMEOUT_MS = 90000;
const MAX_ATTEMPTS = 3;

const api = axios.create({
  baseURL: '/server/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: TABLE_TIMEOUT_MS,
});

api.interceptors.request.use((config) => {
  config.baseURL = `${getApiBaseUrl()}/api`;
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.warn('[API] Session expired (401)', error.config?.url);
    }
    return Promise.reject(error);
  }
);

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryable(error, message = '') {
  const msg = String(message || error?.message || '').toLowerCase();
  const code = String(error?.code || '');
  return (
    msg.includes('timeout') ||
    msg.includes('network') ||
    msg.includes('connection') ||
    msg.includes('econnrefused') ||
    code === 'ECONNABORTED' ||
    code === 'ECONNREFUSED' ||
    code === 'ETIMEDOUT' ||
    code === 'ENOTFOUND' ||
    code === 'ERR_NETWORK'
  );
}

function backoffMs(attempt) {
  return Math.min(1000 * 2 ** attempt, 8000);
}

export const fetchTableData = async (tableName, filters = {}, attempt = 0, maxRetries = MAX_ATTEMPTS) => {
  try {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    const url = `${tableName}${params.toString() ? `?${params.toString()}` : ''}`;

    const response = await api.get(url);

    if (Array.isArray(response.data)) {
      return { success: true, data: response.data };
    }
    if (response.data && response.data.success) {
      return response.data;
    }

    const errorMsg = response.data?.error || 'Unexpected response format from server';
    if (isRetryable(null, errorMsg) && attempt < maxRetries - 1) {
      console.warn(`[API] Retrying ${tableName} (${attempt + 1}/${maxRetries}): ${errorMsg}`);
      await sleep(backoffMs(attempt));
      return fetchTableData(tableName, filters, attempt + 1, maxRetries);
    }

    return {
      success: false,
      error: errorMsg,
      data: [],
      rawResponse: response.data,
    };
  } catch (error) {
    const errorMsg = error.message || 'Failed to fetch data';
    if (isRetryable(error, errorMsg) && attempt < maxRetries - 1) {
      console.warn(`[API] Retrying ${tableName} (${attempt + 1}/${maxRetries}): ${errorMsg}`);
      await sleep(backoffMs(attempt));
      return fetchTableData(tableName, filters, attempt + 1, maxRetries);
    }

    console.warn(`[API] ${tableName} failed:`, errorMsg);
    return {
      success: false,
      error: errorMsg,
      data: [],
    };
  }
};

export const fetchBlockSummary = async (blockId) => {
  try {
    const params = new URLSearchParams();
    params.append('block_id', String(blockId ?? '').trim());
    const response = await axios.get(`${getApiBaseUrl()}/block_summary.php?${params.toString()}`, {
      withCredentials: true,
      timeout: REPORT_TIMEOUT_MS,
    });
    if (response.data && response.data.success !== false) {
      return response.data;
    }
    return {
      success: false,
      error: response.data?.error || 'Failed to load block summary',
      resultSets: [],
    };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to load block summary',
      resultSets: [],
    };
  }
};

export const fetchProductionReport = async (
  reportType,
  startDate = null,
  endDate = null,
  entityId = null,
  attempt = 0,
  maxRetries = MAX_ATTEMPTS
) => {
  try {
    const params = new URLSearchParams();
    params.append('type', reportType);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (entityId) params.append('entityId', entityId);

    const url = `${getApiBaseUrl()}/reports/production.php?${params.toString()}`;
    const response = await axios.get(url, {
      withCredentials: true,
      timeout: REPORT_TIMEOUT_MS,
    });

    if (response.data && response.data.success !== false) {
      return { success: true, data: response.data };
    }

    const errorMsg = response.data?.error || 'Failed to fetch production report';
    if (isRetryable(null, errorMsg) && attempt < maxRetries - 1) {
      console.warn(`[API] Retrying production ${reportType} (${attempt + 1}/${maxRetries}): ${errorMsg}`);
      await sleep(backoffMs(attempt));
      return fetchProductionReport(reportType, startDate, endDate, entityId, attempt + 1, maxRetries);
    }

    return {
      success: false,
      error: errorMsg,
      data: null,
    };
  } catch (error) {
    const errorMsg = error.message || 'Failed to fetch production report';
    if (isRetryable(error, errorMsg) && attempt < maxRetries - 1) {
      console.warn(`[API] Retrying production ${reportType} (${attempt + 1}/${maxRetries}): ${errorMsg}`);
      await sleep(backoffMs(attempt));
      return fetchProductionReport(reportType, startDate, endDate, entityId, attempt + 1, maxRetries);
    }

    console.warn(`[API] Production ${reportType} failed:`, errorMsg);
    return {
      success: false,
      error: errorMsg,
      data: null,
    };
  }
};

export default api;
